import { Button, Sheet, Stack, Text } from '@giftgroup/ui';
import { useEffect, useState } from 'react';
import { notificationsCopy } from '../../copy/notifications';
import { post } from '../../lib/api';
import { useConfig, useMe } from '../../lib/session';
import { enablePush, pushState } from './push';

/** Set by the flows worth celebrating: a first occasion, or a first published list. */
const MOMENT_KEY = 'gg.push.moment';

/**
 * Marks a good moment to ask about notifications (doc 11 › UX). Called from the flows
 * that earn it — never on first load, when the answer is almost always "no".
 */
export function markPushMoment(): void {
  try {
    sessionStorage.setItem(MOMENT_KEY, '1');
  } catch {
    // Private windows can refuse; the prompt just won't appear this session.
  }
}

function hadMoment(): boolean {
  try {
    return sessionStorage.getItem(MOMENT_KEY) === '1';
  } catch {
    return false;
  }
}

function clearMoment(): void {
  try {
    sessionStorage.removeItem(MOMENT_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/**
 * The soft prompt that comes **before** the browser's own (doc 11 › UX). Asking the
 * browser directly costs the one chance to ask: a "no" there is permanent.
 */
export function PushPromptSheet() {
  const copy = notificationsCopy.prompt;
  const { data: me } = useMe();
  const { data: config } = useConfig();
  const [dismissed, setDismissed] = useState(false);
  // Both are read once, at first render: the permission is synchronous, and the moment
  // must survive being cleared below so the sheet doesn't close itself.
  const [state] = useState(() => pushState());
  const [moment] = useState(() => hadMoment());

  const canAsk = state === 'default' || state === 'needs-install';
  const show =
    !dismissed && moment && canAsk && !!me?.pushPrompt.eligible && !!config?.vapidPublicKey;

  useEffect(() => {
    if (!show) return;
    // Side effects only: the moment is spent, and the server records that we asked.
    clearMoment();
    void post('/me/push-prompt', { outcome: 'shown' }).catch(() => undefined);
  }, [show]);

  if (!show) return null;

  // On an iPhone that isn't installed, the answer is instructions, not a permission ask.
  const ios = state === 'needs-install';

  return (
    <Sheet
      open
      onOpenChange={(next) => !next && setDismissed(true)}
      title={ios ? copy.iosTitle : copy.title}
    >
      <Stack gap={4}>
        <Text>{ios ? copy.iosBody : copy.body}</Text>
        <Stack gap={2}>
          {ios ? (
            <Button fullWidth onClick={() => setDismissed(true)}>
              {copy.iosGotIt}
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                onClick={async () => {
                  if (config?.vapidPublicKey) await enablePush(config.vapidPublicKey);
                  setDismissed(true);
                }}
              >
                {copy.turnOn}
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={async () => {
                  setDismissed(true);
                  await post('/me/push-prompt', { outcome: 'snoozed' }).catch(() => undefined);
                }}
              >
                {copy.notNow}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Sheet>
  );
}
